<script lang="ts">
  import { Tesoreria, type CuentaBancaria, type TransaccionBancaria, type Cheque } from '../lib/api';

  let cuentas = $state<CuentaBancaria[]>([]);
  let cuentaSel = $state<string>('');
  let transacciones = $state<TransaccionBancaria[]>([]);
  let cheques = $state<Cheque[]>([]);
  let error = $state<string|null>(null);

  let nuevaCta = $state<CuentaBancaria>({ banco:'', numero:'', alias:'', tipo:'MONETARIA', moneda:'GTQ', activa:true });
  let editandoCtaId = $state<string | null>(null);
  let nuevaTx = $state<TransaccionBancaria>({ cuentaId:'', tipo:'DEPOSITO', monto:0, referencia:'', concepto:'' });
  let nuevoChq = $state<Cheque>({ cuentaId:'', numero:'', beneficiario:'', monto:0, concepto:'' });

  async function cargar() {
    try {
      cuentas = (await Tesoreria.cuentas()).filter(c => c.activa);
      cheques = await Tesoreria.cheques();
      if (cuentaSel) transacciones = await Tesoreria.transac(cuentaSel);
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  function editarCta(c: CuentaBancaria) {
    editandoCtaId = c.id ?? null;
    nuevaCta = { banco: c.banco, numero: c.numero, alias: c.alias ?? '', tipo: c.tipo, moneda: c.moneda ?? 'GTQ', activa: c.activa ?? true };
  }
  function cancelarCta() { editandoCtaId = null; nuevaCta = { banco:'', numero:'', alias:'', tipo:'MONETARIA', moneda:'GTQ', activa:true }; }

  async function guardarCta(e: SubmitEvent) { e.preventDefault();
    try {
      if (editandoCtaId) { await Tesoreria.actualizarCta(editandoCtaId, { ...nuevaCta }); }
      else { await Tesoreria.crearCta({ ...nuevaCta }); }
      nuevaCta = { banco:'', numero:'', alias:'', tipo:'MONETARIA', moneda:'GTQ', activa:true }; editandoCtaId = null;
      await cargar();
    } catch (e:any) { error = e?.message; }
  }
  async function crearTx(e: SubmitEvent) { e.preventDefault();
    try { await Tesoreria.crearTx({ ...nuevaTx, cuentaId: cuentaSel }); nuevaTx = { cuentaId:'', tipo:'DEPOSITO', monto:0, referencia:'', concepto:'' }; await cargar(); } catch (e:any) { error = e?.message; }
  }
  async function emitirCheque(e: SubmitEvent) { e.preventDefault();
    try { await Tesoreria.emitir({ ...nuevoChq, cuentaId: cuentaSel }); nuevoChq = { cuentaId:'', numero:'', beneficiario:'', monto:0, concepto:'' }; await cargar(); } catch (e:any) { error = e?.message; }
  }
  async function conciliar(id: string) { try { await Tesoreria.conciliar(id); await cargar(); } catch (e:any) { error = e?.message; } }
  async function cobrarCh(id: string)  { try { await Tesoreria.cobrarChq(id); await cargar(); } catch (e:any) { error = e?.message; } }
  async function eliminarCta(c: CuentaBancaria) {
    if (!confirm(`¿Eliminar la cuenta ${c.banco} ${c.numero}?`)) return;
    try { await Tesoreria.eliminarCta(c.id!); await cargar(); } catch (e:any) { error = e?.message; }
  }

  $effect(() => { cargar(); });
  $effect(() => { if (cuentaSel) Tesoreria.transac(cuentaSel).then(t => transacciones = t).catch(e => error = e?.message); });
</script>

<section>
  <header class="view-header">
    <div><h2>Tesorería · Bancos · Cheques</h2><p class="muted">Cuentas bancarias, transacciones y cheques</p></div>
    <button onclick={cargar}>Recargar</button>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}

  <h3>Cuentas bancarias</h3>
  <form onsubmit={guardarCta} class="row">
    <label class="field"><span>Banco</span><input placeholder="Banco" bind:value={nuevaCta.banco} required/></label>
    <label class="field"><span>Número</span><input placeholder="Número" bind:value={nuevaCta.numero} required/></label>
    <label class="field"><span>Alias</span><input placeholder="Alias" bind:value={nuevaCta.alias}/></label>
    <label class="field"><span>Tipo</span>
      <select bind:value={nuevaCta.tipo}><option>MONETARIA</option><option>AHORRO</option><option>PLAZO</option></select>
    </label>
    <button type="submit">{editandoCtaId ? 'Guardar' : 'Agregar'}</button>
    {#if editandoCtaId}<button type="button" class="ghost" onclick={cancelarCta}>Cancelar</button>{/if}
  </form>
  <table>
    <thead><tr><th>Banco</th><th>Número</th><th>Alias</th><th>Tipo</th><th>Saldo</th><th></th></tr></thead>
    <tbody>
      {#each cuentas as c (c.id)}
        <tr class:sel={c.id===cuentaSel}>
          <td>{c.banco}</td><td>{c.numero}</td><td>{c.alias ?? ''}</td><td>{c.tipo}</td>
          <td>Q {Number(c.saldoActual ?? 0).toFixed(2)}</td>
          <td><button type="button" class="link" onclick={() => editarCta(c)}>Editar</button> <button onclick={() => cuentaSel = c.id!}>Ver</button> <button type="button" class="link" onclick={() => eliminarCta(c)}>Eliminar</button></td>
        </tr>
      {:else}<tr><td colspan="6" class="empty">Sin cuentas.</td></tr>{/each}
    </tbody>
  </table>

  {#if cuentaSel}
    <h3>Transacciones</h3>
    <form onsubmit={crearTx} class="row">
      <label class="field"><span>Tipo</span>
        <select bind:value={nuevaTx.tipo}>
          <option>DEPOSITO</option><option>RETIRO</option><option>NOTA_DEBITO</option><option>NOTA_CREDITO</option><option>TRANSFERENCIA</option><option>COMISION</option><option>INTERES</option>
        </select>
      </label>
      <label class="field"><span>Monto</span><input type="number" step="0.01" placeholder="Monto" bind:value={nuevaTx.monto} required style="width:130px"/></label>
      <label class="field"><span>Referencia</span><input placeholder="Referencia" bind:value={nuevaTx.referencia}/></label>
      <label class="field grow"><span>Concepto</span><input placeholder="Concepto" bind:value={nuevaTx.concepto} /></label>
      <button type="submit">Registrar</button>
    </form>
    <table>
      <thead><tr><th>Fecha</th><th>Tipo</th><th>Monto</th><th>Concepto</th><th>Conciliada</th><th></th></tr></thead>
      <tbody>
        {#each transacciones as t (t.id)}
          <tr><td>{t.fecha?.slice(0,10) ?? ''}</td><td>{t.tipo}</td><td>Q {Number(t.monto).toFixed(2)}</td><td>{t.concepto ?? ''}</td><td>{t.conciliada ? 'Sí' : ''}</td><td>{#if !t.conciliada}<button onclick={() => conciliar(t.id!)}>Conciliar</button>{/if}</td></tr>
        {:else}<tr><td colspan="6" class="empty">Sin transacciones.</td></tr>{/each}
      </tbody>
    </table>

    <h3>Cheques</h3>
    <form onsubmit={emitirCheque} class="row">
      <label class="field"><span>Número</span><input placeholder="Número" bind:value={nuevoChq.numero} required/></label>
      <label class="field grow"><span>Beneficiario</span><input placeholder="Beneficiario" bind:value={nuevoChq.beneficiario} required /></label>
      <label class="field"><span>Monto</span><input type="number" step="0.01" placeholder="Monto" bind:value={nuevoChq.monto} required style="width:130px"/></label>
      <label class="field"><span>Concepto</span><input placeholder="Concepto" bind:value={nuevoChq.concepto}/></label>
      <button type="submit">Emitir</button>
    </form>
    <table>
      <thead><tr><th>Número</th><th>Beneficiario</th><th>Monto</th><th>Estado</th><th></th></tr></thead>
      <tbody>
        {#each cheques.filter(c=>c.cuentaId===cuentaSel) as c (c.id)}
          <tr><td>{c.numero}</td><td>{c.beneficiario}</td><td>Q {Number(c.monto).toFixed(2)}</td><td>{c.estado}</td>
          <td>{#if c.estado === 'EMITIDO' || c.estado === 'ENTREGADO'}<button onclick={() => cobrarCh(c.id!)}>Marcar cobrado</button>{/if}</td></tr>
        {:else}<tr><td colspan="5" class="empty">Sin cheques en esta cuenta.</td></tr>{/each}
      </tbody>
    </table>
  {/if}
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover { transform: translateY(-1px); }
  .row { display: flex; gap: .75rem; flex-wrap: wrap; margin: 1rem 0; align-items: flex-end; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field.grow { flex: 1; min-width: 180px; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .row input, .row select { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  .row input:focus, .row select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .5rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .9rem; color: #111827; }
  tr.sel { background: #f0fdf4; }
  .err { color: #dc2626; } .empty { color: #9ca3af; text-align: center; font-style: italic; }
  h3 { margin-top: 1.5rem; font-size: 1rem; color: #166534; }
  td button { padding: .25rem .55rem; font-size: .8rem; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #16a34a; cursor: pointer; box-shadow: none; font-weight: 500; }
  td button:hover { background: #f0fdf4; transform: none; }
</style>
